/**
 * useFileDownload — 라이브러리·채팅이 같이 쓰는 파일 받기 훅.
 *
 * 클릭 즉시 버튼을 잠그고 「파일 준비 중…」을 보여 준다. 0.3초 안에 끝나면
 * 스피너가 깜빡이지 않게 최소 표시 시간을 둔다. 오래 걸리면 「조금 더 걸려요」
 * 와 진행률(알 수 있을 때만). 브라우저 저장이 시작되면 토스트, 실패하면
 * 이유를 담은 토스트. 연타로 같은 요청이 두 번 나가지 않는다.
 *
 * 채팅 쪽 파일은 이 훅만 가져다 쓰고, 이 작업에서는 채팅 파일을 건드리지 않는다.
 */

import { useCallback, useRef, useState } from 'react';
import { useToast } from '../components/Toast';
import { downloadAndOpen, downloadBlobFile } from '../utils/downloadUtils';
import { getApiBaseUrl } from '../utils/apiConfig';
import { auth } from '../firebase/config';

export const FILE_DOWNLOAD_MIN_VISIBLE_MS = 300;
export const FILE_DOWNLOAD_SLOW_MS = 2500;

export const FILE_DOWNLOAD_LABELS = Object.freeze({
  preparing: '파일 준비 중…',
  slow: '조금 더 걸려요',
});

export const FILE_DOWNLOAD_TOAST = Object.freeze({
  started: '다운로드를 시작했어요',
  failed: '파일을 만들지 못했어요. 잠시 후 다시 시도해 주세요',
  empty: '받을 파일이 없어요',
  blocked: '팝업이 막혀 파일을 열지 못했어요. 브라우저 설정을 확인해주세요',
});

export class FileDownloadError extends Error {
  constructor(message, { code = 'failed' } = {}) {
    super(message);
    this.name = 'FileDownloadError';
    this.code = code;
    this.userMessage = message;
  }
}

const STORAGE_HOSTS = new Set([
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
]);

const wait = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const sanitizeFilename = (filename, fallback = 'whatquiz-file') => {
  const printableName = Array.from(String(filename || fallback))
    .map((char) => {
      const code = char.charCodeAt(0);
      return code < 32 || code === 127 ? '_' : char;
    })
    .join('');
  return printableName.replace(/[\\/:*?"<>|]/g, '_').slice(0, 120) || fallback;
};

export const absolutizeDownloadUrl = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const hostnameOf = (url) => {
  try {
    return new URL(url, 'http://localhost').hostname;
  } catch (_error) {
    return '';
  }
};

const isStorageUrl = (url) => STORAGE_HOSTS.has(hostnameOf(url));

const isApiPath = (url) => {
  const raw = String(url || '');
  return raw.startsWith('/api/') || /\/api\//.test(raw);
};

const readBlobWithProgress = async (response, onProgress) => {
  const total = Number(response.headers?.get?.('content-length') || 0);
  const reader = total > 0 && response.body && typeof response.body.getReader === 'function'
    ? response.body.getReader()
    : null;
  if (!reader) {
    if (typeof response.blob === 'function') return response.blob();
    throw new FileDownloadError(FILE_DOWNLOAD_TOAST.failed, { code: 'empty_blob' });
  }
  const chunks = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.byteLength || value.length || 0;
      onProgress?.(Math.max(1, Math.min(99, Math.round((received / total) * 100))));
    }
  }
  onProgress?.(100);
  return new Blob(chunks, { type: response.headers?.get?.('content-type') || 'application/octet-stream' });
};

const buildAuthHeaders = async () => {
  const headers = {};
  try {
    const user = auth?.currentUser;
    if (user?.getIdToken) {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }
  } catch (_error) {
    // 비로그인 — 공개 URL만 받는다
  }
  return headers;
};

const messageForStatus = (status) => {
  if (status === 401 || status === 403) return '파일을 받으려면 로그인이 필요해요';
  if (status === 404) return '파일이 더 이상 없어요. 대화에서 다시 저장해주세요';
  if (status === 413) return '파일이 너무 커서 받지 못했어요';
  if (status === 502 || status === 503 || status === 504) {
    return '지금 서버가 바빠요. 잠시 후 다시 시도해주세요';
  }
  return FILE_DOWNLOAD_TOAST.failed;
};

export const fetchDownloadBlob = async (url, filename, { onProgress, signal } = {}) => {
  const abs = absolutizeDownloadUrl(url);
  if (!abs) {
    throw new FileDownloadError(FILE_DOWNLOAD_TOAST.empty, { code: 'empty' });
  }
  if (abs.startsWith('data:')) {
    const response = await fetch(abs, { signal });
    if (!response.ok) {
      throw new FileDownloadError(FILE_DOWNLOAD_TOAST.failed, { code: 'data_uri' });
    }
    return { blob: await response.blob(), filename };
  }

  const headers = await buildAuthHeaders();
  let requestUrl = abs;
  if (isStorageUrl(abs)) {
    const params = new URLSearchParams({
      url: abs,
      filename: sanitizeFilename(filename),
    });
    requestUrl = `${getApiBaseUrl()}/api/gallery/download-proxy?${params}`;
  } else if (!isApiPath(abs) && !abs.startsWith(getApiBaseUrl())) {
    const params = new URLSearchParams({
      url: abs,
      filename: sanitizeFilename(filename),
    });
    requestUrl = `${getApiBaseUrl()}/api/gallery/download-proxy?${params}`;
  }

  let response;
  try {
    response = await fetch(requestUrl, { headers, signal, credentials: 'omit' });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new FileDownloadError('네트워크 연결을 확인한 뒤 다시 시도해주세요', { code: 'network' });
  }

  if (!response.ok) {
    throw new FileDownloadError(messageForStatus(response.status), { code: `http_${response.status}` });
  }

  const blob = await readBlobWithProgress(response, onProgress);
  if (!blob || blob.size === 0) {
    throw new FileDownloadError(FILE_DOWNLOAD_TOAST.failed, { code: 'empty_blob' });
  }
  return { blob, filename };
};

const startBrowserDownload = (blob, filename, url) => {
  const safeName = sanitizeFilename(filename);
  if (blob instanceof Blob) {
    const saved = downloadBlobFile(blob, safeName);
    if (saved === false) {
      throw new FileDownloadError(FILE_DOWNLOAD_TOAST.failed, { code: 'save' });
    }
    return;
  }
  if (!url) {
    throw new FileDownloadError(FILE_DOWNLOAD_TOAST.empty, { code: 'empty' });
  }
  const opened = downloadAndOpen(absolutizeDownloadUrl(url), safeName, { fallbackToSameTab: false });
  if (opened === false) {
    throw new FileDownloadError(FILE_DOWNLOAD_TOAST.blocked, { code: 'popup' });
  }
};

export default function useFileDownload() {
  const { showToast } = useToast();
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);

  const download = useCallback(async (request = {}) => {
    if (inFlightRef.current) {
      return { ok: false, reason: 'busy' };
    }
    inFlightRef.current = true;
    const startedAt = Date.now();
    setStatus('preparing');
    setProgress(null);
    const slowTimer = setTimeout(() => {
      setStatus((prev) => (prev === 'preparing' ? 'slow' : prev));
    }, FILE_DOWNLOAD_SLOW_MS);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    abortRef.current = controller;

    const finishVisible = async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed < FILE_DOWNLOAD_MIN_VISIBLE_MS) {
        await wait(FILE_DOWNLOAD_MIN_VISIBLE_MS - elapsed);
      }
    };

    try {
      const filename = sanitizeFilename(request.filename || 'whatquiz-file');
      let blob = request.blob instanceof Blob ? request.blob : null;
      let url = request.url || '';
      let savedName = filename;

      if (typeof request.getFile === 'function') {
        const produced = await request.getFile({
          signal: controller?.signal,
          onProgress: (value) => {
            if (typeof value === 'number' && Number.isFinite(value)) {
              setProgress(Math.max(0, Math.min(100, Math.round(value))));
            }
          },
        });
        if (produced?.blob instanceof Blob) blob = produced.blob;
        if (produced?.url) url = produced.url;
        if (produced?.filename) savedName = sanitizeFilename(produced.filename);
      }

      if (!blob && url) {
        const fetched = await fetchDownloadBlob(url, savedName, {
          signal: controller?.signal,
          onProgress: (value) => setProgress(value),
        });
        blob = fetched.blob;
        savedName = fetched.filename || savedName;
      }

      if (!blob && !url) {
        throw new FileDownloadError(FILE_DOWNLOAD_TOAST.empty, { code: 'empty' });
      }

      startBrowserDownload(blob, savedName, url);
      await finishVisible();
      const replayUrl = url ? absolutizeDownloadUrl(url) : '';
      showToast(
        FILE_DOWNLOAD_TOAST.started,
        'success',
        6000,
        request.replayAction || (replayUrl
          ? { label: '다시 받기', onClick: () => downloadAndOpen(replayUrl, savedName) }
          : undefined),
      );
      return { ok: true, filename: savedName };
    } catch (error) {
      if (error?.name === 'AbortError') {
        await finishVisible();
        return { ok: false, reason: 'aborted' };
      }
      await finishVisible();
      const message = error?.userMessage || error?.message || FILE_DOWNLOAD_TOAST.failed;
      showToast(message, 'error', 6000);
      return { ok: false, reason: 'error', error };
    } finally {
      clearTimeout(slowTimer);
      inFlightRef.current = false;
      abortRef.current = null;
      setStatus('idle');
      setProgress(null);
    }
  }, [showToast]);

  const buttonLabel = status === 'idle' ? null : (
    status === 'slow' && typeof progress === 'number'
      ? `${FILE_DOWNLOAD_LABELS.slow} ${progress}%`
      : FILE_DOWNLOAD_LABELS[status] || FILE_DOWNLOAD_LABELS.preparing
  );

  return {
    download,
    isDownloading: status !== 'idle',
    status,
    progress,
    buttonLabel,
  };
}
